import React from 'react';
import { Shield, Award, Users, Cog } from 'lucide-react';
import { mockData } from '../mock';

const getQualityIcon = (title) => {
  if (title.includes('Quality')) return <Shield size={24} />;
  if (title.includes('Supply')) return <Award size={24} />;
  if (title.includes('Support')) return <Users size={24} />;
  if (title.includes('Custom')) return <Cog size={24} />;
  return <Shield size={24} />;
};

export const Quality = () => {
  return (
    <section id="quality" className="quality-section">
      <div className="container">
        {/* Quality Features */}
        <div className="quality-content">
          <div className="quality-text">
            <h2 className="heading-1 mb-4">Uncompromising Quality Standards</h2>
            <p className="body-large mb-8">
              At GreenZaar, quality isn't just a promise—it's our foundation. Every batch of guar gum undergoes rigorous testing and quality control to ensure consistent performance for your applications.
            </p>
          </div>
          
          <div className="quality-features">
            {mockData.qualityFeatures.map((feature, index) => (
              <div key={index} className="quality-feature">
                <div className="feature-icon">
                  {getQualityIcon(feature.title)}
                </div>
                <div className="feature-content">
                  <h3 className="heading-3 mb-2">{feature.title}</h3>
                  <p className="body-medium">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Certifications */}
        <div className="certifications-section">
          <h2 className="heading-2 text-center mb-8">Certifications & Compliance</h2>
          <div className="certifications-grid">
            {mockData.certifications.map((cert, index) => (
              <div key={index} className="certification-card network-card">
                <div className="cert-header">
                  <Award className="cert-icon" size={32} />
                  <h3 className="heading-3">{cert.name}</h3>
                </div>
                <p className="body-small mb-4">{cert.description}</p>
                <div className="cert-authority">
                  <span className="authority-label">Certified by:</span>
                  <span className="authority-name">{cert.authority}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Testimonials */}
        <div className="testimonials-section">
          <h2 className="heading-2 text-center mb-8">What Our Clients Say</h2>
          <div className="testimonials-grid">
            {mockData.testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card network-card">
                <div className="testimonial-content">
                  <p className="body-medium mb-6">"{testimonial.feedback}"</p>
                  <div className="testimonial-footer">
                    <div className="company-info">
                      <div className="company-name">{testimonial.company}</div>
                      <div className="company-industry">{testimonial.industry}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Quality Styles
const styles = `
.quality-section {
  background: white;
  padding: var(--spacing-giant) 0;
}

.quality-content {
  margin-bottom: 80px;
}

.quality-text {
  max-width: 800px;
  margin: 0 auto 48px;
  text-align: center;
}

.quality-features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-large);
  max-width: 1000px;
  margin: 0 auto;
}

.quality-feature {
  display: flex;
  gap: 20px;
  padding: var(--spacing-medium);
  background: var(--bg-subtle);
  border-radius: 20px;
  transition: transform 0.3s ease;
}

.quality-feature:hover {
  transform: translateY(-4px);
}

.feature-icon {
  background: white;
  padding: 12px;
  border-radius: 12px;
  color: var(--brand-dark);
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 69, 52, 0.1);
}

.feature-content {
  flex: 1;
}

.certifications-section {
  margin-bottom: 80px;
}

.certifications-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-large);
}

.certification-card {
  text-align: center;
}

.cert-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.cert-icon {
  color: var(--brand-dark);
  background: var(--brand-primary);
  padding: 12px;
  border-radius: 50%;
  width: 56px;
  height: 56px;
}

.cert-authority {
  border-top: 1px solid var(--border-light);
  padding-top: 16px;
  text-align: left;
}

.authority-label {
  font-size: 12px;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 4px;
}

.authority-name {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
}

.testimonials-section {
  background: var(--bg-subtle);
  padding: var(--spacing-giant) var(--spacing-large);
  border-radius: 32px;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--spacing-large);
}

.testimonial-card {
  background: white;
}

.testimonial-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.testimonial-footer {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--border-light);
}

.company-info {
  text-align: left;
}

.company-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.company-industry {
  font-size: 14px;
  color: var(--text-light);
}

@media (max-width: 781px) {
  .quality-features {
    grid-template-columns: 1fr;
    gap: var(--spacing-medium);
  }
  
  .quality-feature {
    flex-direction: column;
    text-align: center;
  }
  
  .certifications-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-medium);
  }
  
  .testimonials-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-medium);
  }
  
  .testimonials-section {
    padding: var(--spacing-xl) var(--spacing-medium);
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}